import {
  IncidentAcknowledgeStatusEnum,
  IncidentDTO,
  IncidentStatusEnum,
  type IncidentStatus,
} from '@eppendorf/vnls-notifications-utils';
import { Icon, IconSizeClasses } from '@eppendorf/vnls-react-components';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';
import { useDebounce, useUnmount } from 'react-use';

import { DeviceNameCell } from '$components/device/name-cell';
import {
  IncidentAcknowledgedDialog,
  IncidentAcknowledgedScreens,
} from '$features/incidents/incident-acknowledged-dialog/incident-acknowledged-dialog';
import { IncidentDetailAcknowledgeFooter } from '$features/incidents/incident-detail-acknowledge-footer';
import { IncidentDetailCard } from '$features/incidents/incident-detail-card';
import { IncidentDetailCardHeaderContent } from '$features/incidents/incident-detail-card-header-content';
import { IncidentDetailFinalState } from '$features/incidents/incident-detail-final-state';
import { IncidentDetailGroupContent } from '$features/incidents/incident-detail-group-content';
import { IncidentDetailMessagesContent } from '$features/incidents/incident-detail-messages-content';
import { IncidentDetailDelayContent } from '$features/incidents/incidents-delay-content';
import {
  AcknowledgeIncidentResponse,
  useAcknowledgeIncident,
  useGetIncident,
  useUpdateIncidentReadStatus,
} from '$features/incidents/incidents.api';

const NOTIFICATION_GROUPS_ORDER: IncidentStatus[] = [
  'NoActionRequired',
  'ActionRequired',
  'Escalated_1',
  'Escalated_2',
];

function calcTimer(startTime: number, delayTimeSeconds: number) {
  let hour = 0;
  let min = 0;
  let sec = 0;
  const endTime = startTime.valueOf() + delayTimeSeconds * 1000;
  if (Date.now() <= endTime) {
    const diff = new Date(endTime - Date.now());
    hour = diff.getUTCHours();
    min = diff.getUTCMinutes();
    sec = diff.getUTCSeconds();
  }
  return { hour, min, sec };
}

function useTimer(startTime: number, delayTimeSeconds: number, isActive: boolean) {
  const [time, setTime] = useState(() => calcTimer(startTime, delayTimeSeconds));

  useEffect(() => {
    if (!isActive) {
      setTime(calcTimer(startTime, delayTimeSeconds));
      return undefined;
    }

    const interval = setInterval(() => {
      setTime(calcTimer(startTime, delayTimeSeconds));
    }, 200);

    return () => clearInterval(interval);
  }, [startTime, delayTimeSeconds, isActive]);

  return time;
}

function wasAcknowledgeWhilePending(data: IncidentDTO | undefined): boolean {
  if (
    data &&
    data.acknowledgedAt &&
    data.createdAt &&
    data.notificationSendDelaySeconds
  ) {
    return (
      data.acknowledgedAt < data.createdAt + 1000 * data.notificationSendDelaySeconds
    );
  }
  return false;
}

export function IncidentFlow() {
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const incidentId = searchParams.get('id') || '';
  const { data } = useGetIncident(incidentId);
  const { mutate } = useUpdateIncidentReadStatus();
  const {
    mutate: acknowledgeIncident,
    isPending: acknowledgingIncident,
    data: acknowledgedIncidentData,
  } = useAcknowledgeIncident();

  const [isAcknowledgedDialogOpen, setIsAcknowledgedDialogOpen] =
    useState<boolean>(false);

  const [acknowledgmentScreen, setAcknowledgmentScreen] =
    useState<IncidentAcknowledgedScreens>(IncidentAcknowledgedScreens.SUCCESS);

  const [, cancel] = useDebounce(
    () => {
      if (!data?.isRead) {
        mutate({ incidentId, options: { markAsRead: true } });
      }
    },
    1000,
    [data],
  );

  useUnmount(cancel);

  const handleAcknowledgeIncident = useCallback(() => {
    acknowledgeIncident(
      { incidentId },
      {
        onSuccess: (response) => {
          if (
            (response as AcknowledgeIncidentResponse).type ===
            IncidentAcknowledgeStatusEnum.AlreadyAcknowledged
          ) {
            // we are leaving out the "already acknowledged" dialog.
          } else {
            setAcknowledgmentScreen(IncidentAcknowledgedScreens.SUCCESS);
            setIsAcknowledgedDialogOpen(true);
          }
        },
        onError: () => {
          setAcknowledgmentScreen(IncidentAcknowledgedScreens.FAILURE);
          setIsAcknowledgedDialogOpen(true);
        },
      },
    );
  }, [incidentId, acknowledgedIncidentData]);

  const toggleAcknowledgedDialog = useCallback(
    (value: boolean) => setIsAcknowledgedDialogOpen(value),
    [],
  );

  const renderTimeline = useMemo(() => {
    if (!data) return null;

    data.notificationGroups.sort(
      (a, b) =>
        NOTIFICATION_GROUPS_ORDER.indexOf(a.incidentStatus) -
        NOTIFICATION_GROUPS_ORDER.indexOf(b.incidentStatus),
    );

    const notificationMessage = data.notificationMessages[0];

    return (
      <>
        <IncidentDetailCard
          deviceCard
          header={
            <div className="flex flex__jc--center p-l">
              <DeviceNameCell device={data.device} />
            </div>
          }
        >
          <div className="flex flex__dir--column gap-s">
            {notificationMessage && (
              <div className="flex flex__ai--center">
                <IncidentDetailMessagesContent
                  notificationMessage={notificationMessage}
                />
              </div>
            )}
            <a
              href={`/monitoring?sidecarRoute=monitoring/parameters&manufacturer=${encodeURIComponent(data.device?.manufacturer ?? 'Eppendorf')}&serialNumber=${encodeURIComponent(data.device?.serialNumber ?? '')}`}
              target="_blank"
              rel="noopener noreferrer"
              className="flex flex__jc--center flex__ai--center gap-xs color-blue-500 font-size-s m-top-xs"
              style={{ textDecoration: 'none', whiteSpace: 'nowrap' }}
            >
              {t('shared.viewMonitoringDetails')}
              <Icon
                name="external-link"
                size={IconSizeClasses.XSmall}
                className="bg-blue-500"
              />
            </a>
          </div>
        </IncidentDetailCard>

        {(data.notificationSendDelaySeconds ?? 0) > 0 && (
          <IncidentDetailCard
            variant={
              data.incidentStatus === IncidentStatusEnum.PendingSendNotification
                ? 'darkblue'
                : 'default'
            }
            header={
              <IncidentDetailCardHeaderContent
                type={
                  data.incidentStatus === IncidentStatusEnum.PendingSendNotification
                    ? IncidentStatusEnum.PendingSendNotification
                    : 'PendingSendNotificationInactive'
                }
              />
            }
            showTail={data.incidentStatus !== IncidentStatusEnum.PendingSendNotification}
          >
            <IncidentDetailDelayContent
              notificationSendDelaySeconds={data.notificationSendDelaySeconds ?? 0}
              startTime={data.createdAt ?? 0}
              isCurrentlyActive={
                data.incidentStatus === IncidentStatusEnum.PendingSendNotification
              }
              useTimer={useTimer}
            />
          </IncidentDetailCard>
        )}

        {data.incidentStatus !== IncidentStatusEnum.PendingSendNotification &&
          data.notificationGroups.map((group, i) => (
            <IncidentDetailCard
              key={group.firstNotificationAt}
              header={<IncidentDetailCardHeaderContent type={group.incidentStatus} />}
              showTail={
                data.notificationGroups.length - 1 !== i ||
                data.incidentStatus === IncidentStatusEnum.Acknowledged ||
                data.incidentStatus === IncidentStatusEnum.Resolved ||
                data.incidentStatus === IncidentStatusEnum.Expired
              }
            >
              <IncidentDetailGroupContent notificationGroup={group} />
            </IncidentDetailCard>
          ))}

        {data.incidentStatus === IncidentStatusEnum.Acknowledged && (
          <IncidentDetailCard
            variant="success"
            header={
              <IncidentDetailCardHeaderContent type={IncidentStatusEnum.Acknowledged} />
            }
            showTail={false}
          >
            <IncidentDetailFinalState timestamp={data.acknowledgedAt}>
              {data.acknowledgedBy ? (
                <div className="font-weight-semibold">{`${data.acknowledgedBy.firstName} ${data.acknowledgedBy.lastName}`}</div>
              ) : (
                <div className="font-weight-semibold">on device</div>
              )}
            </IncidentDetailFinalState>
          </IncidentDetailCard>
        )}
        {data.incidentStatus === IncidentStatusEnum.Resolved && (
          <IncidentDetailCard
            variant="success"
            header={
              <IncidentDetailCardHeaderContent type={IncidentStatusEnum.Resolved} />
            }
            showTail={false}
          >
            <IncidentDetailFinalState timestamp={data.resolvedAt}>
              <div className="font-weight-semibold">
                {t('incidents.autoResolvedByDevice')}
              </div>
            </IncidentDetailFinalState>
          </IncidentDetailCard>
        )}
        {data.incidentStatus === IncidentStatusEnum.Expired && (
          <IncidentDetailCard
            variant="default"
            header={<IncidentDetailCardHeaderContent type={IncidentStatusEnum.Expired} />}
            showTail={false}
          >
            <IncidentDetailFinalState timestamp={data.expiredAt}>
              <Trans
                i18nKey="escalationLayer.expiredEventLabel"
                components={{ bold: <span className="font-weight-semibold" /> }}
              />
            </IncidentDetailFinalState>
          </IncidentDetailCard>
        )}
      </>
    );
  }, [data, data?.acknowledgedAt]);

  return (
    <>
      {renderTimeline}

      <IncidentDetailAcknowledgeFooter
        acknowledgeIncident={handleAcknowledgeIncident}
        acknowledgingIncident={acknowledgingIncident}
        incidentStatus={data?.incidentStatus}
        canAcknowledge={data?.canAcknowledge}
      />

      <IncidentAcknowledgedDialog
        isOpen={isAcknowledgedDialogOpen}
        onOpenChange={toggleAcknowledgedDialog}
        recipientCount={
          wasAcknowledgeWhilePending(data)
            ? 0
            : (data?.notificationGroups[0]?.recipients.length ?? 0)
        }
        screen={acknowledgmentScreen}
        onRetry={handleAcknowledgeIncident}
        acknowledgingIncident={acknowledgingIncident}
      />
    </>
  );
}
